import { useOktaAuth } from "@okta/okta-react";
import dataConstants from "../../constants/dataConstants";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { useEffect, useState } from "react";
import getKeyByValue from "../../services/utils/helpers/keyFromObjectValue";
import * as Yup from "yup";
import moment from "moment";
import dateFormatter from "../../services/utils/helpers/dateFormatter";
import useSchedulePickupAvailability from "../../services/utils/custom hooks/Shipments/useSchedulePickupAvailability";
import PickupInformationFields from "./pickupInformationFields";
import CustomText from "../atomic/CustomText";

const availableTimeAPICallSchema = Yup.object().shape({
  contactName: Yup.string()
    .required("Contact name is required!")
    .test(
      "no-empty-blank-space",
      "Contact name cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  company: Yup.string()
    .required("Company is required!")
    .test(
      "no-empty-blank-space",
      "Company cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  address1: Yup.string()
    .required("Address is required!")
    .test(
      "no-empty-blank-space",
      "Address cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  city: Yup.string()
    .required("City is required!")
    .test(
      "no-empty-blank-space",
      "City cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  stateValue: Yup.string()
    .required("State is required!")
    .test(
      "no-empty-blank-space",
      "State cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  zipCode: Yup.string()
    .min(5, "Zip code length must equal to 5")
    .max(5, "Zip code length must equal to 5")
    .matches(/^[0-9]+$/, "Zip code must be numbers")
    .required("Zip Code is required!"),
  phone: Yup.string()
    .test(
      "phone-validation",
      "Phone must contain 10 digits with the following format 000-000-0000",
      (value) => {
        if (!value) {
          return true;
        }
        return /\d{3}-\d{3}-\d{4}/.test(value);
      }
    )
    .required("Phone is required!"),
  pickupDate: Yup.string()
    .test(
      "custom-date-validator",
      (value: string | undefined) => !!dateFormatter(value)
    )
    .required("Pickup Date is required!"),
});
export interface AvailableTimePayload {
  contactName: string;
  company: string;
  address1: string;
  address2?: string;
  state: string;
  city: string;
  phone: string;
  zipCode: string;
  pickupDate: string;
  closeTime: string;
}

const RequiredValues: AvailableTimePayload = {
  contactName: "",
  company: "",
  address1: "",
  state: "",
  phone: "",
  city: "",
  zipCode: "",
  pickupDate: "",
  closeTime: "",
};

// Removed - now using useSchedulePickupAvailability hook


function PickupInformation({
  initialValues,
  values,
  setFieldValue,
  errors,
  touched,
  setFieldError,
  fetchPatients,
  handleBlur,
  handleChange,
  patients,
  loading,
  ...rest
}: any): JSX.Element {
  const date = new Date();
  const maxDate = date;
  const [availableTimeOptions, setAvailableTimeOptions] = useState<any[]>([]);
  const isRequiredFieldMissing = (updatedValues: any) =>
    Object.keys(RequiredValues).findIndex(
      (key) => updatedValues[key] === ""
    ) !== -1;
  const [fetchingAvailableTime, setFetchingAvailableTime] =
    useState<boolean>(false);
  const schedulePickupAvailability = useSchedulePickupAvailability();


  const convertTo12HourFormat = (time: string) => {
    return moment(time, "HH:mm:ss").format("hh:mm A");
  };

  useEffect(() => {
    if (!isRequiredFieldMissing(values)) {
      availableTimeAPICallSchema
        .validate(values, { abortEarly: false })
        .then((res) => {
          setFetchingAvailableTime(true);
          schedulePickupAvailability.mutate(
            {
              contactName: values.contactName,
              company: values.company,
              address1: values.address1,
              address2: values.address2,
              state: values.stateValue,
              city: values.city,
              phone: values.phone,
              zipCode: values.zipCode,
              pickupDate: values.pickupDate,
              closeTime: getKeyByValue(dataConstants.TimeKey, values.closeTime)
                ? getKeyByValue(dataConstants.TimeKey, values.closeTime)
                : values.closeTime,
            },
            {
              onSuccess: (res: any) => {
              const cutOffTime = res?.data?.options?.[0]?.cutOffTime;

              if (res?.data?.options?.[0]?.readyTimeOptions?.length > 0) {
                const availableTime = res.data.options[0].readyTimeOptions
                  .filter((time: string) => {
                    let currentTime = moment().get("hours");
                    let timeHour = time.split(":")[0];

                    if (
                      values.pickupDate !== moment().format("MM-DD-YYYY") &&
                      time <= cutOffTime
                    ) {
                      return true;
                    }

                    if (
                      availableTimeOptions.includes(time) &&
                      !(Number(timeHour) - currentTime <= 0) &&
                      time <= cutOffTime
                    ) {
                      return true;
                    }

                    return false;
                  })
                  .map((time: string) => ({
                    label: convertTo12HourFormat(time),
                    value: time,
                  }));

                setAvailableTimeOptions(availableTime);

                if (availableTime.length === 0) {
                  toastMessages.error(
                    "FedEx Response: No time available for selected date. Please select another date."
                  );
                }
              }

                setFetchingAvailableTime(false);
              },
              onError: (error: any) => {
                const errorResponse = error.response;
                const errors = error?.response?.data?.errors;
                
                if (
                  error?.response?.data?.errors?.[0]?.code ===
                  "POSTALCODE.INFO.INVALID" ||   
                  error?.response?.data?.errors?.[0]?.code ===
                  "SHIPPER.POSTALSTATE.MISMATCH"
                ) {
                  toastMessages.error("Please enter valid zip code.");
                } else {
                  toastMessages.error(
                    errors?.length > 0
                      ? errors.map((err: any) => err.message).join("")
                      : error.message
                  );
                }
                setFetchingAvailableTime(false);
              },
            }
          );
        })
        .catch((err) => {
          const errors = err.errors;
          return toastMessages.error(errors.join(", "));
        });
    }
  }, [values.phone, values.pickupDate, values.closeTime, values.zipCode]);

  return (
    <div className="w-full relative">
      <CustomText text={"Pickup Information"} fontWeight="font-semibold" />
      <PickupInformationFields
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        availableTimeOptions={availableTimeOptions}
        {...rest}
      />
      {fetchingAvailableTime && (
        <div
          className="top-0 bottom-0 left-0 right-0 w-full h-full absolute"
          style={{ zIndex: 2000 /*  backgroundColor: 'rgba(0,0,0,0.2)' */ }}
        >
          <div className={`flex w-full h-full justify-center items-center`}>
            <div className="animate-spin">
              <img
                src={require("../../assets/images/loadingPNG.png")}
                className={"h-[50px] w-[50px]"}
                alt="loading indicator"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PickupInformation;
